import { ReactNode } from "react";
import { Alert, AlertTitle } from "@mui/material";
import { ErrorBoundary as SignalBoundary } from "@appsignal/react";

import { appSignal } from "@/appsignal";
import { useTranslation } from "@/lib/i18n";

type FallbackRender = (error: Error) => ReactNode;

type Props = {
  children: ReactNode;
  /** Extra AppSignal tags merged with the default `{ app: "admin" }`. */
  tags?: Record<string, string>;
  /**
   * Custom fallback. Receives the thrown error (the @appsignal/react boundary
   * calls this with the error directly, not wrapped in an object).
   */
  fallback?: FallbackRender;
};

/**
 * Component-level error boundary for the admin area. Intercepts render errors
 * before they bubble to the root React Router error element (which would replace
 * the whole shell with the global 404) and reports them to AppSignal.
 */
export const AdminErrorBoundary = ({ children, tags, fallback }: Props) => {
  return (
    <SignalBoundary
      instance={appSignal}
      tags={{ app: "admin", ...tags }}
      fallback={fallback ?? ((error: Error) => <DefaultFallback error={error} />)}
    >
      {children}
    </SignalBoundary>
  );
};

const DefaultFallback = ({ error }: { error: Error }) => {
  const { t } = useTranslation();
  return (
    <Alert variant="outlined" severity="error">
      <AlertTitle>{t("admin.error_boundary.title")}</AlertTitle>
      {error?.message || t("admin.error_boundary.description")}
    </Alert>
  );
};
